import { noop } from '@kc-console/common-ui';

import { DataTableProvider } from '../../context/DataTableContext';
import DataTableTableBody from './DataTableTableBody';
import DataTableTableHead from './DataTableTableHead';
import useColumnVirtualization from './hooks/useColumnVirtualization';
import useDataTableAdapter from './useDataTableAdapter';
import useDataTableEmptyState from './useDataTableEmptyState';

import type { ChangeEvent, MouseEvent, ReactNode, RefObject, TouchEvent } from 'react';
import type { RowData, RowSelectionState, Table, Updater } from '@tanstack/react-table';
import type { CheckedState } from '@kc-console/common-ui';
import type {
  DataTableColumnVirtualization,
  DataTableColumnVirtualizationState,
  DataTableProps,
  SortState,
} from './types';

interface DataTableTableContext<TData extends RowData> {
  table: Table<TData>;
  tableContainerRef: RefObject<HTMLDivElement | null>;
  tableContainerWidth: number | null;
  columnVirtualization?: DataTableColumnVirtualization;
}

interface DataTableDisplayState<TData extends RowData> {
  hasFilters: boolean;
  renderFallbackValue: string;
  showSideBorder: boolean;
  disabledExpandTooltip?: string;
  disabledRowTooltip?: string;
  emptyState?: DataTableProps<TData>['emptyState'];
  emptyStateProps?: DataTableProps<TData>['emptyStateProps'];
  error?: unknown;
  errorState?: DataTableProps<TData>['errorState'];
  isError?: boolean;
  isLoading?: boolean;
  layoutType?: DataTableProps<TData>['layoutType'];
}

interface DataTableFeatureState {
  hasAutoPadColumn: boolean;
  hasExpandColumn: boolean;
  hasSelectionColumn: boolean;
  hasSelectionColumnAsCheckbox: boolean;
  hasSelectionColumnAsRadio: boolean;
  selectionHeaderCheckedState: CheckedState;
  showExpandAllButton: boolean;
  sorting: SortState[];
}

interface DataTableInteractions {
  contextMenuEnabled: boolean;
  onColumnResize: (
    event: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>,
    columnId: string,
    cellElement: HTMLTableCellElement,
  ) => void;
  onColumnSizeReset: (columnId: string) => void;
  onExpandHeaderChange: (event: MouseEvent<HTMLButtonElement>, checked: boolean) => void;
  onRowClick: (event: MouseEvent<HTMLTableRowElement>, rowId: string) => void;
  onRowContextMenu: (event: MouseEvent<HTMLTableRowElement>, rowId: string) => void;
  onRowExpandingToggle: (rowId: string, expanded?: boolean) => void;
  onRowSelectionChange: (updater: Updater<RowSelectionState>) => void;
  onRowSelectionToggle: (rowId: string) => void;
  onSelectionHeaderChange: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  onSortDirectionChange: (columnId: string, sortDirection: 'asc' | 'desc' | false) => void;
  onRefetch?: () => void;
  renderExpandedRow?: (params: { open: boolean; rowId: string }) => ReactNode;
}

interface DataTableTableContentsProps<TData extends RowData> {
  displayState: DataTableDisplayState<TData>;
  featureState: DataTableFeatureState;
  interactions: DataTableInteractions;
  tableContext: DataTableTableContext<TData>;
}

/** DataTable의 header와 body를 일반 또는 열 가상화 경로로 렌더링 */
export default function DataTableTableContents<TData extends RowData>(
  props: DataTableTableContentsProps<TData>,
) {
  const { displayState, featureState, interactions, tableContext } = props;
  const { columnVirtualization } = tableContext;

  if (columnVirtualization) {
    return (
      <DataTableVirtualizedTableContents
        columnVirtualization={columnVirtualization}
        displayState={displayState}
        featureState={featureState}
        interactions={interactions}
        tableContext={tableContext}
      />
    );
  }

  return (
    <DataTableResolvedTableContents
      displayState={displayState}
      featureState={featureState}
      interactions={interactions}
      tableContext={tableContext}
    />
  );
}

function DataTableVirtualizedTableContents<TData extends RowData>(
  props: DataTableTableContentsProps<TData> & {
    columnVirtualization: DataTableColumnVirtualization;
  },
) {
  const { columnVirtualization, displayState, featureState, interactions, tableContext } = props;
  const { table, tableContainerRef } = tableContext;
  const columnVirtualizationState = useColumnVirtualization({
    columnVirtualization,
    table,
    tableContainerRef,
  });

  return (
    <DataTableResolvedTableContents
      columnVirtualizationState={columnVirtualizationState}
      displayState={displayState}
      featureState={featureState}
      isColumnVirtualizationEnabled
      interactions={interactions}
      tableContext={tableContext}
    />
  );
}

function DataTableResolvedTableContents<TData extends RowData>(
  props: DataTableTableContentsProps<TData> & {
    columnVirtualizationState?: DataTableColumnVirtualizationState;
    isColumnVirtualizationEnabled?: boolean;
  },
) {
  const {
    columnVirtualizationState,
    displayState,
    featureState,
    interactions,
    isColumnVirtualizationEnabled,
    tableContext,
  } = props;
  const {
    disabledExpandTooltip,
    disabledRowTooltip,
    emptyState,
    emptyStateProps,
    error,
    errorState,
    hasFilters,
    isError,
    isLoading,
    layoutType,
    renderFallbackValue,
    showSideBorder,
  } = displayState;
  const {
    hasAutoPadColumn,
    hasExpandColumn,
    hasSelectionColumn,
    hasSelectionColumnAsCheckbox,
    hasSelectionColumnAsRadio,
    selectionHeaderCheckedState,
    showExpandAllButton,
    sorting,
  } = featureState;
  const {
    contextMenuEnabled,
    onColumnResize,
    onColumnSizeReset,
    onExpandHeaderChange,
    onRefetch,
    onRowClick,
    onRowContextMenu,
    onRowExpandingToggle,
    onRowSelectionChange,
    onRowSelectionToggle,
    onSelectionHeaderChange,
    onSortDirectionChange,
    renderExpandedRow,
  } = interactions;
  const { table, tableContainerWidth } = tableContext;
  const { headerGroups, headerCount, rows } = useDataTableAdapter({
    columnVirtualization: columnVirtualizationState,
    hasExpandColumn,
    hasSelectionColumn,
    isColumnVirtualizationEnabled,
    sorting,
    table,
  });
  const {
    emptyState: resolvedEmptyState,
    emptyStateProps: resolvedEmptyStateProps,
    emptyStateType,
  } = useDataTableEmptyState({
    emptyState,
    emptyStateProps,
    error,
    errorState,
    hasFilters,
    isError,
    isLoading,
    layoutType,
    rowsLength: rows.length,
    showSideBorder,
  });
  const renderHeaderCount = hasAutoPadColumn ? headerCount + 1 : headerCount;

  return (
    <>
      <DataTableTableHead
        columnVirtualization={columnVirtualizationState}
        expandHeaderState={rows.some(row => row.expanded)}
        hasAutoPadColumn={hasAutoPadColumn}
        hasExpandColumn={hasExpandColumn}
        hasSelectionHeader={hasSelectionColumn}
        hasSelectionHeaderAsCheckbox={hasSelectionColumnAsCheckbox}
        headerGroups={headerGroups}
        onSelectionHeaderChange={onSelectionHeaderChange}
        onColumnResize={onColumnResize}
        onColumnSizeReset={onColumnSizeReset}
        onExpandHeaderChange={onExpandHeaderChange}
        onSortDirectionChange={onSortDirectionChange}
        showExpandAllButton={showExpandAllButton}
        selectionHeaderCheckedState={selectionHeaderCheckedState}
      />

      <DataTableProvider
        compactMode={table.getState().compactMode}
        textEllipsis={table.getState().textEllipsis}
      >
        <DataTableTableBody
          columnVirtualization={columnVirtualizationState}
          contextMenuEnabled={contextMenuEnabled}
          disabledExpandTooltip={disabledExpandTooltip}
          disabledRowTooltip={disabledRowTooltip}
          emptyState={resolvedEmptyState}
          emptyStateType={emptyStateType}
          hasExpandColumn={hasExpandColumn}
          hasSelectionColumn={hasSelectionColumn}
          hasSelectionColumnAsCheckbox={hasSelectionColumnAsCheckbox}
          hasSelectionColumnAsRadio={hasSelectionColumnAsRadio}
          headerCount={renderHeaderCount}
          hasAutoPadColumn={hasAutoPadColumn}
          onRefetch={onRefetch ?? noop}
          onRowClick={onRowClick}
          onRowContextMenu={onRowContextMenu}
          onRowExpandingToggle={onRowExpandingToggle}
          onRowSelectionChange={onRowSelectionChange}
          onRowSelectionToggle={onRowSelectionToggle}
          renderExpandedRow={renderExpandedRow}
          renderFallbackValue={renderFallbackValue}
          rows={rows}
          tableContainerWidth={emptyStateType ? tableContainerWidth : null}
          textEllipsis={table.getState().textEllipsis}
          emptyStateProps={resolvedEmptyStateProps}
        />
      </DataTableProvider>
    </>
  );
}
